// UnitColumnsStep.js
//
// Step 2 of the unit view, Columns: the Map process's decisions, one per column of the
// table, each naming the source that fills it — a column of this unit, a header field of
// its sheet, a value the file shares, or a formula over them — or nothing (Not present).
//
// Changes are a draft, held here, until Apply writes every changed column in one Map edit;
// Discard drops the draft. The notes beside each column are worked out from the mapping as
// it would stand after Apply, so the person sees what the draft leaves to do before
// applying it. The draft is never written on its own: when the table stops being editable
// before it is applied, it is set aside until the person restores or discards it
// (UnitStepFrame's useStepDraft).
//
//     draft = { [target]: { kind: 'source', value: 'Policy No' | null }
//                       | { kind: 'formula', elements: [{ type: 'variable' | 'operator', value }] } }

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import FormulaBuilder, { formulaSyntaxError } from './FormulaBuilder';
import UnitStepFrame, {
  ApplyBar, SetAsideNotice, cellText, sourceLabel, targetLabel, useStepDraft,
} from './UnitStepFrame';
import { activeValue, activeVersion } from '../../../utils/decisions';
import { editUnitDecisions, requestUnit, resetUnitDecision } from '../../../services/cleaningGestures';
import {
  buildDestinationTypeMap,
  buildRequiredScopeMap,
  deriveMappedSample,
  deriveTypeWarnings,
  elementsToFormula,
  formulaToElements,
  getMappingTargetColumns,
  getUnmappedMandatoryTargets,
  isFormulaMapping,
  sameMappingValue,
} from '../../../utils/mappingUtils';
import { externalReference } from '../../../utils/externalReferences';
import { isRawTable } from '../../../utils/tableTypes';

const MAP = 'map';
const PERIOD = 'period';
const FORMULA_OPTION = '__formula__';
const FORMULA_SAMPLE_ROWS = 3;

const TYPE_PLURALS = { Number: 'numbers', Date: 'dates', 'Yes/No': 'yes or no values' };

// Props: number, companyId, tableId, unit, table, verdicts, editBlock, and the unit view's
// draftEpoch and runUnitChange(send, onApplied).
export default function UnitColumnsStep({
  number,
  companyId,
  tableId,
  unit,
  table,
  verdicts,
  editBlock,
  draftEpoch,
  runUnitChange,
}) {
  const { changes: draft, setAsideChanges, change, clear, restore } = useStepDraft(draftEpoch, {});
  const [applying, setApplying] = useState(false);
  const [resettingTarget, setResettingTarget] = useState(null);

  const mapRecord = unit.processes?.[MAP] || null;
  const targets = getMappingTargetColumns(table);
  const identifierName = isRawTable(table) ? null : table.identifier?.name;
  const typeMap = buildDestinationTypeMap(table);
  const scopes = buildRequiredScopeMap(table);

  // Everything a source may name on this unit, grouped as the select lists them.
  const columnSources = unit.columns || [];
  const fieldSources = Object.keys(unit.fields || {}).map((key) => externalReference('Sheet', key));
  const poolSources = Object.keys(unit.pool || {});
  const attributeNames = Object.keys(unit.attributes || {});
  const formulaVariables = [...columnSources, ...fieldSources, ...poolSources, ...attributeNames];
  const knownNames = new Set(formulaVariables);
  const listedSources = new Set([...columnSources, ...fieldSources, ...poolSources]);

  const liveValue = (target) => (mapRecord ? activeValue(mapRecord, target) : null);
  const valueOf = (entry) => (entry.kind === 'formula' ? elementsToFormula(entry.elements) : entry.value);
  const draftValue = (target) => (draft[target] ? valueOf(draft[target]) : liveValue(target));

  // What a formula in the draft still needs before it can be applied.
  const formulaProblem = (target) => {
    const entry = draft[target];
    if (!entry || entry.kind !== 'formula') return '';
    if (entry.elements.length === 0) return 'Build the formula, or choose a column.';
    const syntax = formulaSyntaxError(entry.elements);
    if (syntax) return syntax;
    const unknown = entry.elements.find((element) => element.type === 'variable' && !knownNames.has(element.value));
    if (unknown) {
      return `'${sourceLabel(unknown.value)}' is not a column, header field, shared value or attribute of this table.`;
    }
    return '';
  };

  // The columns a set-aside draft would still change, measured against the mapping as it
  // now stands. While there are any, the columns cannot be edited until the person
  // restores the draft or discards it.
  const setAsideTargets = setAsideChanges === null ? [] : targets.filter((target) => (
    setAsideChanges[target] && !sameMappingValue(valueOf(setAsideChanges[target]), liveValue(target))
  ));
  const disabled = Boolean(editBlock) || applying || setAsideTargets.length > 0;

  const draftMapping = {};
  targets.forEach((target) => { draftMapping[target] = draftValue(target); });
  const changedTargets = targets.filter((target) => (
    draft[target] && !sameMappingValue(draftValue(target), liveValue(target))
  ));
  const formulaProblems = targets.map(formulaProblem).filter(Boolean);
  const stillRequired = new Set(getUnmappedMandatoryTargets(draftMapping, table));
  const typeWarnings = deriveTypeWarnings(draftMapping, table, unit.column_stats);
  const metricMapped = targets.some((target) => (
    target.startsWith(`${PERIOD}.`) && !sameMappingValue(draftMapping[target], null)
  ));

  const chooseSource = (target, selected) => {
    if (selected === FORMULA_OPTION) {
      const live = liveValue(target);
      const elements = isFormulaMapping(live) ? formulaToElements(live) : [];
      change((previous) => ({ ...previous, [target]: { kind: 'formula', elements } }));
      return;
    }
    const value = selected || null;
    change((previous) => {
      const next = { ...previous };
      if (sameMappingValue(value, liveValue(target))) delete next[target];
      else next[target] = { kind: 'source', value };
      return next;
    });
  };

  const apply = async () => {
    const entries = changedTargets.map((target) => ({
      key: target,
      value: sameMappingValue(draftValue(target), null) ? null : draftValue(target),
      refersTo: null,
    }));
    setApplying(true);
    await runUnitChange(() => editUnitDecisions(companyId, tableId, unit.id, MAP, entries), clear);
    setApplying(false);
  };

  const undo = async (target) => {
    setResettingTarget(target);
    await runUnitChange(() => resetUnitDecision(companyId, tableId, unit.id, MAP, target));
    setResettingTarget(null);
  };

  const renderRow = (target) => {
    const version = mapRecord ? activeVersion(mapRecord, target) : null;
    const entry = draft[target];
    const changed = changedTargets.includes(target);
    // A formula is being built in the draft, or is the mapping as it stands.
    const formulaMode = entry ? entry.kind === 'formula' : isFormulaMapping(liveValue(target));
    const current = draftValue(target);
    const problem = formulaProblem(target);
    // FormulaBuilder shows its own grammar error; any other problem is shown here.
    const builderShowsProblem = Boolean(entry?.kind === 'formula' && formulaSyntaxError(entry.elements));
    const suggested = version?.made_by === 'process' && version?.produced?.suggested === true;
    const yours = version?.made_by === 'person';
    let formulaPreview = null;
    if (formulaMode && !problem && isFormulaMapping(current)) {
      const results = deriveMappedSample({
        rows: (unit.sample_rows || []).slice(0, FORMULA_SAMPLE_ROWS),
        mapping: { [target]: current },
        fields: unit.fields,
        pool: unit.pool,
        table,
        attributes: unit.attributes,
      });
      formulaPreview = results.map((row) => cellText(row[target])).join(' · ');
    }

    // The caption under the column's name: what kind of column it is and, for a detail
    // column or a metric, the scopes it is required in.
    let caption;
    if (target === identifierName) {
      caption = 'identifier';
    } else if (target === PERIOD) {
      caption = 'reporting period';
    } else {
      const scope = scopes[target];
      const parts = [(typeMap[target] || 'Text').toLowerCase()];
      if (scope?.perFile && scope?.perUnit) parts.push('required per file and per unit');
      else if (scope?.perUnit) parts.push('required per unit');
      else if (scope?.perFile) parts.push('required per file');
      caption = parts.join(' · ');
    }

    // The notes beside the column, from the mapping as it would stand after Apply.
    const notes = [];
    const scope = scopes[target];
    const mapped = !sameMappingValue(draftMapping[target], null);
    if (stillRequired.has(target)) {
      let text = 'required per unit';
      if (target === identifierName) text = 'required';
      else if (target === PERIOD) text = 'required while a metric is mapped';
      else if (isRawTable(table) && !scope?.perUnit) text = 'required per file';
      notes.push({ text, colour: 'warning.main' });
    } else if (!mapped && scope?.perFile && !isRawTable(table)) {
      notes.push({ text: 'required per file: another table of this file can supply it', colour: 'text.secondary' });
    }
    if (target === PERIOD && mapped && !metricMapped && !isRawTable(table)) {
      notes.push({ text: 'map a metric, or leave this unmapped', colour: 'warning.main' });
    }
    // "3 of 398 values are not numbers ('N/A', 'TBC')", from deriveTypeWarnings. A
    // formula's operands are read as numbers whatever the column's type.
    typeWarnings.filter((warning) => warning.column === target).forEach((warning) => {
      const kind = warning.kind === 'formula' ? 'numbers' : (TYPE_PLURALS[warning.declaredType] || 'valid');
      const operand = warning.kind === 'formula' ? `${sourceLabel(warning.source)}: ` : '';
      if (warning.allFail) {
        notes.push({ text: `${operand}none of the values are ${kind}`, colour: 'error.main' });
        return;
      }
      const examples = warning.examples.length
        ? ` (${warning.examples.map((example) => `'${example}'`).join(', ')})`
        : '';
      notes.push({
        text: `${operand}${warning.failures} of ${warning.present} values are not ${kind}${examples}`,
        colour: 'warning.main',
      });
    });

    // The select's options, grouped; a source the unit's copies no longer list still shows
    // as what is chosen.
    const options = [<MenuItem key="not-present" value="">Not present</MenuItem>];
    const group = (label, sources) => {
      if (sources.length === 0) return;
      options.push(<ListSubheader key={`group-${label}`}>{label}</ListSubheader>);
      sources.forEach((source) => options.push(
        <MenuItem key={`${label}-${source}`} value={source}>{sourceLabel(source)}</MenuItem>,
      ));
    };
    group('Columns in this table', columnSources);
    group('Header fields', fieldSources);
    group('Shared values', poolSources);
    if (!formulaMode && current && !listedSources.has(current)) group('Chosen before', [current]);
    options.push(<MenuItem key="formula" value={FORMULA_OPTION}>A formula…</MenuItem>);

    return (
      <TableRow key={target} sx={changed ? { bgcolor: 'action.hover' } : undefined}>
        <TableCell sx={{ width: 220, verticalAlign: 'top' }}>
          <Typography variant="body2" fontWeight={600}>{targetLabel(target)}</Typography>
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            disabled={disabled}
            value={formulaMode ? FORMULA_OPTION : (current || '')}
            onChange={(event) => chooseSource(target, event.target.value)}
            renderValue={(selected) => {
              if (selected === FORMULA_OPTION) return 'A formula…';
              return sourceLabel(selected);
            }}
            SelectDisplayProps={{ 'aria-label': `Source of ${targetLabel(target)}` }}
          >
            {options}
          </Select>
          {formulaMode && (
            <Box sx={{ mt: 1 }}>
              <FormulaBuilder
                elements={entry?.kind === 'formula' ? entry.elements : formulaToElements(current || '')}
                onChange={(elements) => change((previous) => ({ ...previous, [target]: { kind: 'formula', elements } }))}
                availableVariables={formulaVariables}
                disabled={disabled}
              />
              {problem && !builderShowsProblem && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{problem}</Typography>
              )}
              {formulaPreview && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {`First rows give: ${formulaPreview}`}
                </Typography>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ width: 110, verticalAlign: 'top' }}>
          {changed && <Chip size="small" color="info" variant="outlined" label="changed" />}
          {!changed && suggested && (
            <Tooltip title="Decode's guess from this document. Approving teaches it for next time.">
              <Chip size="small" color="secondary" variant="outlined" label="Suggested" />
            </Tooltip>
          )}
          {!changed && !suggested && yours && <Chip size="small" variant="outlined" label="You" />}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          {notes.map((note) => (
            <Typography key={note.text} variant="caption" color={note.colour} sx={{ display: 'block' }}>
              {note.text}
            </Typography>
          ))}
        </TableCell>
        <TableCell sx={{ width: 70, verticalAlign: 'top' }}>
          {yours && !unit.approved && (
            <Button
              size="small"
              disabled={disabled || resettingTarget === target}
              onClick={() => undo(target)}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              Undo
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const tableName = String(table.name || table.id || '').replace(/_/g, ' ');

  return (
    <UnitStepFrame
      number={number}
      title="Columns"
      verdicts={verdicts}
      record={mapRecord}
      menu
      editBlock={editBlock}
      onRequest={(kind) => runUnitChange(() => requestUnit(companyId, tableId, unit.id, MAP, kind))}
      defaultOpen
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {unit.approved
          ? 'Approved: un-approve the table to change its columns.'
          : `Which column of this table fills each column of ${tableName}. `
            + 'Decode fills these in from past uploads; change any, then press Apply.'}
      </Typography>
      <SetAsideNotice
        changeCount={setAsideTargets.length}
        editBlock={editBlock}
        onRestore={restore}
        onDiscard={clear}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{`${tableName} column`}</TableCell>
              <TableCell>Filled from</TableCell>
              <TableCell />
              <TableCell>Notes</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {targets.map(renderRow)}
          </TableBody>
        </Table>
      </TableContainer>
      <ApplyBar
        changeCount={changedTargets.length}
        editBlock={editBlock}
        applying={applying}
        applyDisabledReason={formulaProblems.length ? 'Finish or remove the formula first.' : ''}
        onApply={apply}
        onDiscard={clear}
      />
    </UnitStepFrame>
  );
}
